import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IconArrowLeft, IconSend } from "@tabler/icons-react";
import { mutate } from "swr";
import { mutateByPrefix } from "../../../../swr/useApi";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { ClaimStatusChip } from "../components/claim-status-chip";
import { useSupplierClaimDetails } from "../../hooks/useSupplierWarrantyClaims";
import { warrantyClaimsSellerService } from "../../../api_client";
import type { UpdateClaimStatusDto, WarrantyClaimMessageDto } from "../../../../api";
import { UpdateClaimStatusDtoStatusEnum } from "../../../../api";

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function formatDate(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  const d = iso instanceof Date ? iso : new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : DATE_FMT.format(d);
}

const TYPE_LABEL: Record<string, string> = {
  DAMAGE: "Damage",
  DEFECT: "Defect",
  MISSING_PART: "Missing Part",
  DOES_NOT_MATCH_DESCRIPTION: "Does Not Match Description",
  OTHER: "Other",
};

const NEXT_STATUSES: Record<string, UpdateClaimStatusDtoStatusEnum[]> = {
  NEW: [UpdateClaimStatusDtoStatusEnum.Acknowledged, UpdateClaimStatusDtoStatusEnum.Rejected],
  ACKNOWLEDGED: [UpdateClaimStatusDtoStatusEnum.Investigating, UpdateClaimStatusDtoStatusEnum.Resolved, UpdateClaimStatusDtoStatusEnum.Rejected],
  INVESTIGATING: [UpdateClaimStatusDtoStatusEnum.Resolved, UpdateClaimStatusDtoStatusEnum.Rejected],
  RESOLVED: [],
  REJECTED: [],
};

export function WarrantyClaimDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const cacheKey = id ? `supplier-warranty-claim-${id}` : null;
  const { data: claim, isLoading } = useSupplierClaimDetails(id ?? null);

  async function handleSendMessage() {
    if (!id || !replyText.trim()) return;
    setSending(true);
    try {
      await warrantyClaimsSellerService.addSupplierClaimMessage(id, {
        content: replyText.trim(),
      });
      setReplyText("");
      await mutate(cacheKey);
    } catch {
      // TODO: show toast
    } finally {
      setSending(false);
    }
  }

  async function handleStatusUpdate(status: UpdateClaimStatusDtoStatusEnum) {
    if (!id) return;
    setUpdatingStatus(true);
    try {
      await warrantyClaimsSellerService.updateSupplierClaimStatus(id, { status } as UpdateClaimStatusDto);
      await mutate(cacheKey);
      await mutateByPrefix("supplier-warranty-claims");
      await mutateByPrefix("supplier-warranty-claim-stats");
    } catch {
      // TODO: show toast
    } finally {
      setUpdatingStatus(false);
    }
  }

  if (isLoading || !claim) {
    return (
      <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8 max-md:px-4 max-md:py-6">
        <ShimmerLoader message="Loading claim details…" lines={8} />
      </div>
    );
  }

  const nextStatuses = NEXT_STATUSES[claim.status] ?? [];
  const messages = (claim.messages ?? []) as WarrantyClaimMessageDto[];

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8 max-md:px-4 max-md:py-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(AppRoutes.WARRANTY_CLAIMS)}
          className="flex items-center gap-1 text-textSecondary hover:text-textDark transition-colors"
        >
          <IconArrowLeft size={18} />
          <Text variant="bodySm" tone="body">
            Back to claims
          </Text>
        </button>
      </div>

      <div className="flex items-start justify-between">
        <div>
          <Heading variant="h2" tone="dark">
            Warranty Claim
          </Heading>
          <Text variant="bodySm" tone="muted" className="font-mono mt-0.5">
            {claim.id}
          </Text>
        </div>
        <ClaimStatusChip status={claim.status} />
      </div>

      {/* Claim info */}
      <div className="rounded-2xl border border-borderDefault bg-bgWhite p-6 flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Text variant="bodySmBold" tone="dark">
              Claim Type
            </Text>
            <Text variant="body" tone="body">
              {TYPE_LABEL[claim.claimType] ?? claim.claimType}
            </Text>
          </div>
          <div>
            <Text variant="bodySmBold" tone="dark">
              Sale Order
            </Text>
            <Text variant="body" tone="body">
              #{claim.saleOrderId}
            </Text>
          </div>
          <div>
            <Text variant="bodySmBold" tone="dark">
              Filed
            </Text>
            <Text variant="body" tone="body">
              {formatDate(claim.createdAt)}
            </Text>
          </div>
          <div>
            <Text variant="bodySmBold" tone="dark">
              Last Updated
            </Text>
            <Text variant="body" tone="body">
              {formatDate(claim.updatedAt)}
            </Text>
          </div>
        </div>
        <div>
          <Text variant="bodySmBold" tone="dark">
            Description
          </Text>
          <Text variant="body" tone="body" className="mt-1">
            {claim.description}
          </Text>
        </div>
      </div>

      {/* Status actions */}
      {nextStatuses.length > 0 && (
        <div className="rounded-2xl border border-borderDefault bg-bgWhite p-4 flex items-center gap-3">
          <Text variant="bodySm" tone="body">
            Update status:
          </Text>
          {nextStatuses.map((s) => (
            <Button
              key={s}
              variant="secondary"
              size="sm"
              disabled={updatingStatus}
              onClick={() => handleStatusUpdate(s)}
            >
              {s.charAt(0) + s.slice(1).toLowerCase()}
            </Button>
          ))}
        </div>
      )}

      {/* Message thread */}
      <div className="rounded-2xl border border-borderDefault bg-bgWhite flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-borderDefault">
          <Heading variant="h3" tone="dark">
            Message Thread
          </Heading>
        </div>

        {messages.length === 0 ? (
          <div className="px-6 py-8 flex items-center justify-center">
            <Text variant="body" tone="muted">
              No messages yet. Add the first response below.
            </Text>
          </div>
        ) : (
          <div className="flex flex-col divide-y divide-borderDefault">
            {messages.map((m) => (
              <div
                key={m.id}
                className={`px-6 py-4 ${m.authorRole === "seller" ? "bg-accentCyanTint/30" : ""}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Text variant="bodySmBold" tone="dark">
                    {m.authorRole === "seller" ? "You (Seller)" : "Buyer"}
                  </Text>
                  <Text variant="bodyXs" tone="muted">
                    {formatDate(m.createdAt)}
                  </Text>
                </div>
                <Text variant="body" tone="body">
                  {m.content}
                </Text>
              </div>
            ))}
          </div>
        )}

        {/* Reply box */}
        {(claim.status === "NEW" || claim.status === "ACKNOWLEDGED" || claim.status === "INVESTIGATING") && (
          <div className="px-6 py-4 border-t border-borderDefault flex flex-col gap-3">
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Write a response to the buyer…"
              rows={3}
              className="w-full rounded-xl border border-borderDefault bg-bgLight px-4 py-3 text-sm text-textDark placeholder:text-textMuted resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <div className="flex justify-end">
              <Button
                variant="primary"
                size="sm"
                leftIcon={<IconSend size={15} />}
                disabled={!replyText.trim() || sending}
                onClick={handleSendMessage}
              >
                {sending ? "Sending…" : "Send Response"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default WarrantyClaimDetailsPage;
